import type { NavbarWithCtaButton } from '@flotiq/flotiq-flowbite';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { _translate } from '@flotiq/nextjs-internationalization';

import { getEnv } from '../../lib/env';
import { getFlotiqPageNav } from '../../lib/flotiq-client/external-services-requests';
import { parseResponseErrors } from '../../lib/flotiq-client/response-errors';
import { getFlotiqMainPageUrl } from '../../lib/helpers';

import topnavPlaceholderData from './placeholder-data/topnav.json';

const QUERY_KEY = ['flotiq_page_space', 'top_nav'];

const NAV_LANGUAGES = [
  { language: 'English', url_prefix: 'en' },
  { language: 'Polski', url_prefix: 'pl' },
];

export const useTopNav = () => {
  const {
    isLoading: requestLoading,
    data: response,
    isPlaceholderData,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async ({ signal }) => {
      const result = await getFlotiqPageNav(
        undefined,
        undefined,
        { hydrate: 1 },
        {
          signal,
        },
      );
      if (!result.ok) {
        throw parseResponseErrors(result.body, result.status);
      }
      return result;
    },
    enabled:
      !!getEnv('VITE_FLOTIQ_PAGE_SPACE_X_AUTH_TOKEN') &&
      !!getEnv('VITE_FLOTIQ_PAGE_NAV_API_URL'),
    staleTime: Infinity,
    gcTime: 0,
    retry: 0,
  });

  const { i18n } = useTranslation();

  const isLoading = requestLoading || isPlaceholderData;

  const topNavData = response?.ok
    ? _translate<typeof NavbarWithCtaButton>(response.body, i18n.language)
    : topnavPlaceholderData;

  const finalTopNav =
    'items' in topNavData
      ? {
          ...topNavData,
          languages: NAV_LANGUAGES,
          items: topNavData.items.map((item) => ({
            ...item,
            link: item.link?.startsWith('/')
              ? getFlotiqMainPageUrl('/en' + item.link)
              : item.link,
            sub_items:
              item.sub_items?.map((subItem) => ({
                ...subItem,
                link: subItem.link?.startsWith('/')
                  ? getFlotiqMainPageUrl('/en' + subItem.link)
                  : subItem.link,
              })) || [],
          })),
        }
      : { ...topNavData, languages: NAV_LANGUAGES };

  return {
    isLoading: isLoading,
    topNav: finalTopNav,
  };
};
